import React, { useEffect, useRef, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import LeaveVessel from "@/components/LeaveVessel";
import BlockedMembers from "@/components/BlockedMembers";
import PageHeader from "@/components/PageHeader";
import MemberAvatar from "@/components/MemberAvatar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Camera, Loader2, ShieldCheck, Save, Smartphone, Sailboat, IdCard, KeyRound, Download } from "lucide-react";
import Crest from "@/components/Crest";
import { OrderBadge, ORDERS } from "@/components/Beret";
import { downloadBlob } from "@/lib/download";
import { toast } from "sonner";

const FRIGATES = ["Cherokee", "Cumberland", "Pelican"];
const POSITIONS = ["Member", "Frigate Captain", "GM", "Penmate", "Mascot", "Crier", "Scribe", "Purse", "Galleon Purse", "FC"];
const GALLEON_POSITIONS = ["Galleon Purse", "FC"];
const MONTHS = ["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"];
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DAYS = Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, "0"));

export default function Profile() {
  const { user, refresh, setUser } = useAuth();
  const [form, setForm] = useState({ name: "", deck_name: "", phone: "", bio: "", frigate: "", position: "Member", order: "" });
  const [bmonth, setBmonth] = useState("");
  const [bday, setBday] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const [twofaOpen, setTwofaOpen] = useState(false);
  const [cardUrl, setCardUrl] = useState(null);
  const [devices, setDevices] = useState([]);
  const [phraseOpen, setPhraseOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [newPhrase, setNewPhrase] = useState("");
  useEffect(() => {
    if (!user?.member_id) return;
    let u;
    api.get("/auth/id-card.png", { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setCardUrl(u); }).catch(() => {});
    api.get("/auth/trusted-devices").then((r) => setDevices(r.data.devices)).catch(() => {});
    return () => u && URL.revokeObjectURL(u);
  }, [user?.member_id, user?.photo_path, user?.position, user?.frigate]);
  const changePhrase = async () => {
    try { const { data } = await api.post("/auth/onboard", { phrase: newPhrase, current_password: pw }); setUser(data.user); setPhraseOpen(false); setPw(""); setNewPhrase(""); toast.success("Security phrase updated"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const forgetDevices = async () => { await api.delete("/auth/trusted-devices"); setDevices([]); toast.success("All devices forgotten — the phrase will be asked next sign-in"); };
  const forgetDevice = async (id) => { await api.delete(`/auth/trusted-devices/${id}`); setDevices((ds) => ds.filter((d) => d.id !== id)); toast.success("Device forgotten"); };
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [enabling, setEnabling] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({ name: user.name || "", deck_name: user.deck_name || "", phone: user.phone || "",
        bio: user.bio || "", frigate: user.frigate || "", position: user.position || "Member", order: user.order || "" });
      if (user.birthday && user.birthday.includes("-")) {
        const [mm, dd] = user.birthday.split("-");
        setBmonth(mm); setBday(dd);
      }
    }
  }, [user]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, frigate: form.frigate };
      if (bmonth && bday) payload.birthday = `${bmonth}-${bday}`;
      await api.put("/profile", payload);
      await refresh();
      toast.success("Profile updated");
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try { const fd = new FormData(); fd.append("file", f); await api.post("/profile/photo", fd); await refresh(); toast.success("Photo updated"); }
    catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const startSetup = async () => { try { const { data } = await api.post("/auth/2fa/setup"); setSetup(data); setTwofaOpen(true); } catch (e) { toast.error(errMsg(e)); } };
  const enable2fa = async () => {
    setEnabling(true);
    try { await api.post("/auth/2fa/enable", { content: code }); await refresh(); setTwofaOpen(false); setCode(""); toast.success("2FA enabled"); }
    catch (e) { toast.error(errMsg(e)); } finally { setEnabling(false); }
  };
  const toggle2fa = async (on) => {
    if (on) startSetup();
    else { await api.post("/auth/2fa/disable"); await refresh(); toast.success("2FA disabled"); }
  };

  const isGalleonAdmin = user?.is_galleon_admin;
  const initials = (user?.name || "?").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase(); // eslint-disable-line no-unused-vars

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader eyebrow="Your Account" title="Profile & Security"
        description="Your deck name, frigate, officer position, and account security." />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="gold-ring glass flex flex-col items-center p-6 text-center">
          <div className="relative">
            <Avatar className="h-28 w-28 ring-2 ring-[hsl(var(--gold))]/40">
              <MemberAvatar name={user?.name} photo={user?.photo_path} className="h-28 w-28" testid="profile-avatar" />
            </Avatar>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={uploadPhoto} />
            <Button size="icon" className="absolute bottom-0 right-0 h-9 w-9 rounded-full bg-[hsl(var(--gold))] text-[hsl(var(--primary-foreground))]" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-photo-button">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
            </Button>
          </div>
          <h3 className="mt-4 font-display text-xl font-700">{user?.name}</h3>
          {user?.deck_name && <p className="text-sm italic text-muted-foreground">"{user.deck_name}"</p>}
          <p className="text-sm text-muted-foreground">{user?.email}</p>
          <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
            <Badge className="bg-[hsl(var(--gold))] text-[hsl(var(--primary-foreground))]">{user?.position}</Badge>
            <Badge variant="outline" className="border-[hsl(var(--water))]/40 text-[hsl(var(--water))]"><Sailboat className="mr-1 h-3 w-3" />{user?.frigate || "Galleon"}</Badge>
          </div>
          {user?.order && <OrderBadge order={user.order} className="mt-2" testid="profile-order-badge" />}
        </Card>

        <Card className="gold-ring glass p-6 lg:col-span-2">
          <h3 className="mb-4 font-display text-lg font-700">Edit Details</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div><Label>Full Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="profile-name-input" /></div>
            <div><Label>Deck Name</Label><Input value={form.deck_name} onChange={(e) => setForm({ ...form, deck_name: e.target.value })} className="mt-1" placeholder="e.g. Stormrider" data-testid="profile-deck-input" /></div>
            <div>
              <Label>Frigate</Label>
              <Select value={form.frigate || "none"} onValueChange={(v) => setForm({ ...form, frigate: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1" data-testid="profile-frigate-select"><SelectValue placeholder="Choose frigate" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Galleon (none)</SelectItem>
                  {FRIGATES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Officer Position</Label>
              <Select value={form.position} onValueChange={(v) => setForm({ ...form, position: v })}>
                <SelectTrigger className="mt-1" data-testid="profile-position-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {POSITIONS.map((p) => (
                    <SelectItem key={p} value={p} disabled={GALLEON_POSITIONS.includes(p) && !isGalleonAdmin}>{p}{GALLEON_POSITIONS.includes(p) && !isGalleonAdmin ? " (admin only)" : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Order</Label>
              <Select value={form.order || "none"} onValueChange={(v) => setForm({ ...form, order: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1" data-testid="profile-order-select"><SelectValue placeholder="Choose order" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not set</SelectItem>
                  {ORDERS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Birthday Month</Label>
              <Select value={bmonth} onValueChange={setBmonth}>
                <SelectTrigger className="mt-1" data-testid="profile-bmonth-select"><SelectValue placeholder="Month" /></SelectTrigger>
                <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={m}>{MONTH_NAMES[i]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Birthday Day</Label>
              <Select value={bday} onValueChange={setBday}>
                <SelectTrigger className="mt-1" data-testid="profile-bday-select"><SelectValue placeholder="Day" /></SelectTrigger>
                <SelectContent>{DAYS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-4"><Label>Bio</Label><Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="mt-1" data-testid="profile-bio-input" /></div>
          <p className="mt-2 text-xs text-muted-foreground">Note: choosing a frigate officer position grants you that position's powers within your frigate.</p>
          <Button onClick={save} disabled={saving} className="mt-4 bg-[hsl(var(--gold))] font-700 text-[hsl(var(--primary-foreground))]" data-testid="save-profile-button">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-2 h-4 w-4" /> Save Changes</>}
          </Button>
        </Card>

        <Card className="gold-ring glass p-6 lg:col-span-3" id="id-card" data-testid="id-card-section">
          <div className="flex items-center gap-2"><IdCard className="h-5 w-5 text-[hsl(var(--gold))]" /><h3 className="font-display text-lg font-700">Member ID Card</h3><Crest position={user?.position} size={30} className="ml-auto" /></div>
          {user?.member_id ? (
            <div className="mt-4 grid grid-cols-1 gap-5 md:grid-cols-[1fr_260px]">
              <div className="overflow-hidden rounded-2xl border border-[hsl(var(--gold))]/40 bg-black/5">
                {cardUrl ? <img src={cardUrl} alt="Member ID card" className="w-full" data-testid="id-card-image" /> : <div className="flex h-48 items-center justify-center text-sm text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" />Rendering your card…</div>}
              </div>
              <div className="space-y-3">
                <div><p className="label-eyebrow">Member ID</p><p className="font-mono-data text-2xl font-700 tracking-wider" data-testid="profile-member-id">{user.member_id}</p></div>
                <Button className="w-full bg-[hsl(var(--gold))] font-700 text-[hsl(var(--primary-foreground))]" onClick={() => downloadBlob("/auth/id-card.png", `IOTA-ID-${user.member_id}.png`)} data-testid="save-id-card-btn"><Download className="mr-2 h-4 w-4" />Save to phone</Button>
                <p className="text-xs text-muted-foreground">Tip: on your phone, tap Save then add the image to your wallet/photos for quick access at gatherings.</p>
                <div className="rounded-lg border border-[hsl(var(--gold))]/20 p-3">
                  <p className="flex items-center gap-1.5 text-sm font-600"><KeyRound className="h-4 w-4 text-[hsl(var(--gold))]" />Security phrase</p>
                  <p className="text-xs text-muted-foreground">Required on new devices after your password. You get an alert whenever a new device signs in.</p>
                  {devices.length > 0 && (
                    <ul className="mt-2 space-y-1" data-testid="trusted-devices-list">
                      {devices.map((d) => (
                        <li key={d.id || d.created_at} className="flex items-center justify-between gap-2 rounded-md bg-secondary/60 px-2 py-1 text-xs" data-testid={`device-${d.id}`}>
                          <span className="truncate"><Smartphone className="mr-1 inline h-3 w-3" />{d.label || d.ua?.slice(0, 40)} · {d.ip} · last {new Date(d.last_used).toLocaleDateString()}</span>
                          {d.id && <button onClick={() => forgetDevice(d.id)} className="shrink-0 text-destructive hover:underline" data-testid={`forget-device-btn-${d.id}`}>Forget</button>}
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="mt-2 flex gap-2">
                    <Button size="sm" variant="outline" className="gold-ring" onClick={() => setPhraseOpen(true)} data-testid="change-phrase-btn">Change phrase</Button>
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={forgetDevices} data-testid="forget-devices-btn">Forget all devices</Button>
                  </div>
                </div>
              </div>
            </div>
          ) : <p className="mt-3 text-sm text-muted-foreground">Your Member ID will be issued when you complete the security prompt.</p>}
        </Card>

        <Card className="gold-ring glass p-6 lg:col-span-3">
          <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-[hsl(var(--gold))]" /><h3 className="font-display text-lg font-700">Two-Factor Authentication</h3></div>
          <div className="mt-4 flex items-center justify-between rounded-lg border border-[hsl(var(--gold))]/20 p-4">
            <div className="flex items-center gap-3">
              <Smartphone className="h-6 w-6 text-[hsl(var(--water))]" />
              <div>
                <p className="text-sm font-600">Authenticator App (TOTP)</p>
                <p className="text-xs text-muted-foreground">{user?.twofa_enabled ? "Enabled — codes required at login." : "Add an extra layer of security."}</p>
              </div>
            </div>
            <Switch checked={!!user?.twofa_enabled} onCheckedChange={toggle2fa} data-testid="twofa-toggle" />
          </div>
        </Card>

        <BlockedMembers />
        <LeaveVessel />
      </div>

      <Dialog open={phraseOpen} onOpenChange={setPhraseOpen}>
        <DialogContent className="glass" data-testid="change-phrase-dialog">
          <DialogHeader><DialogTitle className="font-display">Change security phrase</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Current password</Label><Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="phrase-password-input" /></div>
            <div><Label>New security phrase</Label><Input type="password" value={newPhrase} onChange={(e) => setNewPhrase(e.target.value)} autoComplete="off" data-testid="new-phrase-input" /></div>
          </div>
          <Button onClick={changePhrase} disabled={newPhrase.trim().length < 4 || !pw} className="bg-[hsl(var(--gold))] text-[hsl(var(--primary-foreground))]" data-testid="save-phrase-btn">Save phrase</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={twofaOpen} onOpenChange={setTwofaOpen}>
        <DialogContent className="glass">
          <DialogHeader><DialogTitle className="font-display">Set Up 2FA</DialogTitle></DialogHeader>
          {setup && (
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Scan this QR with Google Authenticator, Authy, or similar.</p>
              <img src={setup.qr} alt="2FA QR" className="mx-auto my-4 h-44 w-44 rounded-lg bg-white p-2" data-testid="twofa-qr" />
              <code className="mt-1 block break-all rounded bg-secondary px-2 py-1 font-mono-data text-xs">{setup.secret}</code>
              <p className="mt-4 text-sm font-600">Enter the 6-digit code to confirm:</p>
              <div className="mt-2 flex justify-center">
                <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="twofa-confirm-input">
                  <InputOTPGroup>{[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} />)}</InputOTPGroup>
                </InputOTP>
              </div>
              <Button onClick={enable2fa} disabled={enabling || code.length < 6} className="mt-4 w-full bg-[hsl(var(--gold))] text-[hsl(var(--primary-foreground))]" data-testid="confirm-2fa-button">
                {enabling ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enable 2FA"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
